'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }, 1500);
  };

  const services = [
    { value: '', label: 'Bitte wählen Sie eine Leistung' },
    { value: 'kanzlei', label: 'Internationale Kanzlei-Kooperationen' },
    { value: 'firmengruendung', label: 'Firmengründung & Auswanderung Dubai' },
    { value: 'immobilien', label: 'Immobilien-Vernetzung' },
    { value: 'beratung', label: 'Allgemeine Beratung' },
  ];

  return (
    <section className="py-32 px-6 md:px-10 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: Form */}
          <div className="lg:col-span-7 fade-in-up will-animate">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs font-bold uppercase tracking-ultra text-muted-foreground mb-4"
                >
                  Ihr Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-luxury"
                  placeholder="Max Mustermann"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-bold uppercase tracking-ultra text-muted-foreground mb-4"
                >
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-luxury"
                  placeholder="ihre@email.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-xs font-bold uppercase tracking-ultra text-muted-foreground mb-4"
                >
                  Telefon / WhatsApp
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-luxury"
                  placeholder="+49 123 456789"
                />
              </div>

              {/* Service */}
              <div>
                <label
                  htmlFor="service"
                  className="block text-xs font-bold uppercase tracking-ultra text-muted-foreground mb-4"
                >
                  Interesse an
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="input-luxury cursor-pointer"
                >
                  {services.map((service) => (
                    <option key={service.value} value={service.value}>
                      {service.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-xs font-bold uppercase tracking-ultra text-muted-foreground mb-4"
                >
                  Ihre Nachricht *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="input-luxury resize-none"
                  placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
              </button>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-success/10 text-success">
                  <Icon name="CheckCircleIcon" size={24} />
                  <p className="text-sm font-medium">
                    Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei
                    Ihnen.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Right: Sidebar Info */}
          <div className="lg:col-span-5 space-y-8 fade-in-up will-animate">
            <div className="card-luxury p-10">
              <h3 className="font-serif text-2xl font-medium mb-6 text-foreground">
                Kostenlose Erstberatung
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                Nutzen Sie unser kostenloses 30-minütiges Erstgespräch, um Ihre
                Ziele zu besprechen und einen ersten Eindruck von unserer
                Arbeitsweise zu bekommen.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <Icon
                    name="ClockIcon"
                    size={24}
                    className="text-accent flex-shrink-0"
                  />
                  <div>
                    <h5 className="font-medium text-sm mb-1">Schnelle Antwort</h5>
                    <p className="text-xs text-muted-foreground">
                      Innerhalb von 24 Stunden
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Icon
                    name="ShieldCheckIcon"
                    size={24}
                    className="text-accent flex-shrink-0"
                  />
                  <div>
                    <h5 className="font-medium text-sm mb-1">Vertraulich</h5>
                    <p className="text-xs text-muted-foreground">
                      Absolute Diskretion garantiert
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Icon
                    name="CheckCircleIcon"
                    size={24}
                    className="text-accent flex-shrink-0"
                  />
                  <div>
                    <h5 className="font-medium text-sm mb-1">Unverbindlich</h5>
                    <p className="text-xs text-muted-foreground">
                      Keine versteckten Kosten
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Alternative Contact */}
            <div className="p-8 rounded-2xl bg-background border border-border">
              <h4 className="font-serif text-lg font-medium mb-4 text-foreground">
                Lieber direkt anrufen?
              </h4>
              <p className="text-sm text-muted-foreground mb-6">
                Wir sind auch telefonisch oder per WhatsApp erreichbar.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="tel:+971501234567"
                  className="inline-flex items-center gap-3 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                >
                  <Icon name="PhoneIcon" size={20} />
                  +971 50 123 4567
                </a>
                <a
                  href="https://wa.me/971501234567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={20} />
                  WhatsApp Chat
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}